import type { PartnerOrganization } from '@reach/shared-types/workos-auth';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield,
  UserX,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { partnerApi, type OrganizationsResponse } from '../lib/api';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';

/**
 * Restricted Access Page
 * Shown to users who have been added to the organization but don't have sufficient permissions
 */
export const RestrictedAccessPage: React.FC = () => {
  const { user, organization, logout } = useAuth();
  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
  const [switchingToOrgId, setSwitchingToOrgId] = useState<string | null>(null);
  const [paginationCursor, setPaginationCursor] = useState<{
    before?: string;
    after?: string;
  }>({});

  const ORGS_PER_PAGE = 5;

  // Check if user has multiple organizations with pagination
  const {
    data: organizationsResponse,
    isLoading: isLoadingOrgs,
    isFetching: isFetchingOrgs,
  } = useQuery<OrganizationsResponse>({
    queryKey: ['organizations', paginationCursor],
    queryFn: () =>
      partnerApi.getOrganizations({
        limit: ORGS_PER_PAGE,
        ...paginationCursor,
      }),
    enabled: !!user,
    placeholderData: previousData => previousData, // Keep previous data visible while loading
  });

  // Type assertion to handle schema update - data now uses 'results' instead of 'organizations'
  const data = organizationsResponse?.data as
    | {
        results: PartnerOrganization[];
        pagination: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          cursor: string | null | undefined;
          previousCursor: string | null | undefined;
        };
      }
    | undefined;
  const organizations = data?.results || [];
  const pagination = data?.pagination;
  // Show switcher if user has multiple orgs on current page, or if there are more pages (indicating more orgs exist)
  const hasMultipleOrganizations =
    organizations.length > 1 ||
    !!pagination?.hasNextPage ||
    !!pagination?.hasPreviousPage;

  // Check if we're on initial load (no pagination cursor)
  const isInitialLoad = !paginationCursor.before && !paginationCursor.after;

  // Hide pagination on initial load if we have <= ORGS_PER_PAGE and no next page
  // Otherwise show pagination if there's a next or previous page
  const shouldHideOnInitialLoad =
    isInitialLoad &&
    organizations.length <= ORGS_PER_PAGE &&
    !pagination?.hasNextPage;

  const showPagination =
    !shouldHideOnInitialLoad &&
    (pagination?.hasNextPage || pagination?.hasPreviousPage);

  // Reset pagination when dropdown closes
  useEffect(() => {
    if (!isOrgSwitcherOpen) {
      setPaginationCursor({});
    }
  }, [isOrgSwitcherOpen]);

  const handleSwitchOrganization = async (orgId: string) => {
    if (orgId === organization?.id) {
      setIsOrgSwitcherOpen(false);
      return;
    }

    setSwitchingToOrgId(orgId);

    try {
      const data = await partnerApi.switchOrganization(orgId);

      if (data.data.requiresReauth) {
        window.location.href = data.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      setSwitchingToOrgId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="mx-auto h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <UserX className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl">Access Restricted</CardTitle>
          <CardDescription className="text-base">
            Your access to this dashboard is currently limited
          </CardDescription>

          {/* Organization Switcher in header */}
          {hasMultipleOrganizations && (
            <div className="mt-4 flex justify-center relative">
              <button
                onClick={() => setIsOrgSwitcherOpen(!isOrgSwitcherOpen)}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-card border border-border rounded-md hover:bg-accent transition-colors">
                <Building2 className="w-4 h-4" />
                <span className="max-w-[150px] truncate">
                  {organization?.name || 'Select Organization'}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isOrgSwitcherOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOrgSwitcherOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsOrgSwitcherOpen(false)}
                  />

                  {/* Dropdown */}
                  <div className="absolute top-full mt-2 w-64 bg-card rounded-lg shadow-lg border border-border z-20 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">
                        Switch Organization
                      </p>
                    </div>
                    <div className="py-1">
                      {isLoadingOrgs && organizations.length === 0 ? (
                        <div className="px-4 py-3 flex items-center space-x-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Loading...</span>
                        </div>
                      ) : (
                        <>
                          {organizations.map((org: PartnerOrganization) => {
                            const isActive = org.id === organization?.id;
                            const isSwitching = switchingToOrgId === org.id;

                            return (
                              <button
                                key={org.id}
                                onClick={() => handleSwitchOrganization(org.id)}
                                disabled={isSwitching}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-accent transition-colors ${
                                  isActive ? 'bg-accent' : ''
                                } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-foreground truncate">
                                    {org.name}
                                  </p>
                                  {org.domain && (
                                    <p className="text-xs text-muted-foreground truncate">
                                      {org.domain}
                                    </p>
                                  )}
                                </div>
                                {isSwitching ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-primary ml-2 flex-shrink-0" />
                                ) : isActive ? (
                                  <Check className="w-4 h-4 text-primary ml-2 flex-shrink-0" />
                                ) : null}
                              </button>
                            );
                          })}
                          {/* Pagination Controls */}
                          {showPagination && (
                            <div className="border-t border-border px-3 py-2 flex items-center justify-between bg-muted/50">
                              <button
                                onClick={() => {
                                  if (pagination?.previousCursor) {
                                    setPaginationCursor({
                                      before: pagination.previousCursor,
                                      after: undefined,
                                    });
                                  }
                                }}
                                disabled={
                                  !pagination?.hasPreviousPage || isFetchingOrgs
                                }
                                className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Previous page">
                                {isFetchingOrgs ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ChevronLeft className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  if (pagination?.cursor) {
                                    setPaginationCursor({
                                      after: pagination.cursor,
                                      before: undefined,
                                    });
                                  }
                                }}
                                disabled={
                                  !pagination?.hasNextPage || isFetchingOrgs
                                }
                                className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Next page">
                                {isFetchingOrgs ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Signed in as:
              </span>
              <span className="text-sm font-semibold">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Organization:
              </span>
              <span className="text-sm font-semibold">
                {organization?.name || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">
                  Limited Access Account
                </h3>
                <p className="text-sm text-muted-foreground">
                  You've been successfully added to{' '}
                  {organization?.name || 'this organization'}, but you currently
                  have minimal access permissions.
                </p>
              </div>
            </div>

            {hasMultipleOrganizations && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-900 mb-2">
                      Try Switching Organizations
                    </p>
                    <p className="text-sm text-green-700">
                      You belong to multiple organizations. If you have admin
                      access in another organization, use the organization
                      switcher above to switch.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="border-l-4 border-primary bg-primary/5 p-4 rounded-r">
              <p className="text-sm font-medium mb-2">What can you do?</p>
              <p className="text-sm text-muted-foreground">
                To access the full partner dashboard features, please contact
                your organization administrator and request elevated
                permissions.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center pt-4">
            <Button onClick={logout} variant="outline">
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
